import { combineReducers } from "redux";
import constant from "lodash-es/constant";
import get from "lodash-es/get";

import createReducer from "../../utils/createReducer";

import {
  REQUEST_DEL_PHYSICAL_DAMAGE_FAIL,
  REQUEST_DEL_PHYSICAL_DAMAGE_SUCCESS,
  REQUEST_DEL_PHYSICAL_DAMAGE,
  REQUEST_DELETE_IMG_FAIL,
  REQUEST_DELETE_IMG_SUCCESS,
  REQUEST_DELETE_IMG,
  REQUEST_HEADERS_FAIL,
  REQUEST_HEADERS_SUCCESS,
  REQUEST_HEADERS,
  REQUEST_IMG_FAIL,
  REQUEST_IMG_SUCCESS,
  REQUEST_IMG,
  REQUEST_PHYSICAL_DAMAGE_FAIL,
  REQUEST_PHYSICAL_DAMAGE_SUCCESS,
  REQUEST_PHYSICAL_DAMAGE,
  REQUEST_PHYSICAL_DAMAGES_FAIL,
  REQUEST_PHYSICAL_DAMAGES_SUCCESS,
  REQUEST_PHYSICAL_DAMAGES,
  REQUEST_UPLOAD_ANNOTATED_FAIL,
  REQUEST_UPLOAD_ANNOTATED_SUCCESS,
  REQUEST_UPLOAD_ANNOTATED,
  REQUEST_UPLOAD_DESCRIPTION_FAIL,
  REQUEST_UPLOAD_DESCRIPTION_SUCCESS,
  REQUEST_UPLOAD_DESCRIPTION,
  REQUEST_UPLOAD_IMG_FAIL,
  REQUEST_UPLOAD_IMG_SUCCESS,
  REQUEST_UPLOAD_IMG,
  REQUEST_UPLOAD_ESTIMATED_COST_FAIL,
  REQUEST_UPLOAD_ESTIMATED_COST_SUCCESS,
  REQUEST_UPLOAD_ESTIMATED_COST,
  RESET_IMG_UPLOADS,
  UPDATE_IMG_PROGRESS
} from "./actionTypes";
import { CLEAR_MEDIA_UPLOAD_PROGRESS } from "../caseDetail/actionTypes";

// Physical Damages (list)
const currentCase = createReducer(null, {
  [REQUEST_PHYSICAL_DAMAGES]: (state, { payload }) => payload.caseId
});

const list = createReducer(null, {
  [REQUEST_PHYSICAL_DAMAGES_SUCCESS]: (state, { payload }) => payload
});

const isLoadingList = createReducer(false, {
  [REQUEST_PHYSICAL_DAMAGES]: constant(true),
  [REQUEST_PHYSICAL_DAMAGES_SUCCESS]: constant(false),
  [REQUEST_PHYSICAL_DAMAGES_FAIL]: constant(false)
});

const listError = createReducer(null, {
  [REQUEST_PHYSICAL_DAMAGES]: constant(null),
  [REQUEST_PHYSICAL_DAMAGES_FAIL]: (state, { payload }) => payload
});

// Physical Damage data
const data = createReducer(null, {
  [REQUEST_PHYSICAL_DAMAGE_SUCCESS]: (state, { payload }) => payload,
  [REQUEST_IMG_SUCCESS]: (state, { payload }) => {
    const newState = JSON.parse(JSON.stringify(state));
    const { index, image } = payload;

    if (index || index === 0) {
      const { isProcessing, downloadURLs } = get(
        newState,
        `images.${index}`,
        {}
      );
      // Ignores the fetched URLs when comparing - a still-processing poll
      // gets a freshly re-signed URL on every tick even though nothing
      // meaningful changed, and replacing on that would retrigger effects
      // keyed on this image's identity forever. forceReplace opts out of
      // that dedup for callers that explicitly want a fresh signed URL
      // (e.g. recovering from an expired one).
      const shouldReplace =
        payload.forceReplace ||
        !downloadURLs ||
        isProcessing !== image.isProcessing;

      if (shouldReplace) newState.images[index] = image;
      else return state;
    } else newState.images.push(payload.image);

    return newState;
  },
  [REQUEST_DELETE_IMG_SUCCESS]: (state, { payload }) => {
    const newState = JSON.parse(JSON.stringify(state));
    const images = newState.images.filter(
      ({ filename }) => filename !== payload
    );
    return { ...newState, images };
  }
});

const error = createReducer(null, {
  [REQUEST_PHYSICAL_DAMAGE]: constant(null),
  [REQUEST_PHYSICAL_DAMAGE_FAIL]: (state, { payload }) => payload
});

const isLoading = createReducer(false, {
  [REQUEST_PHYSICAL_DAMAGE]: constant(true),
  [REQUEST_PHYSICAL_DAMAGE_SUCCESS]: constant(false),
  [REQUEST_PHYSICAL_DAMAGE_FAIL]: constant(false)
});

const isLoadingImage = createReducer(false, {
  [REQUEST_IMG]: constant(true),
  [REQUEST_IMG_SUCCESS]: constant(false),
  [REQUEST_IMG_FAIL]: constant(false)
});

const requestImageError = createReducer(false, {
  [REQUEST_IMG]: constant(false),
  [REQUEST_IMG_FAIL]: constant(true)
});

const isDeletingPhysicalDamage = createReducer(false, {
  [REQUEST_DEL_PHYSICAL_DAMAGE]: constant(true),
  [REQUEST_DEL_PHYSICAL_DAMAGE_SUCCESS]: constant(false),
  [REQUEST_DEL_PHYSICAL_DAMAGE_FAIL]: constant(false)
});

// Image Headers
const imageHeaders = createReducer(
  {},
  {
    [REQUEST_HEADERS]: (state, { payload }) => ({
      ...state,
      [payload]: null
    }),
    [REQUEST_HEADERS_FAIL]: (state, { payload }) => ({
      ...state,
      [payload]: null
    }),
    [REQUEST_HEADERS_SUCCESS]: (state, { payload }) => ({
      ...state,
      [payload.filename]: payload.data
    })
  }
);

const loadingHeaders = createReducer(
  {},
  {
    [REQUEST_HEADERS]: (state, { payload }) => {
      const newState = JSON.parse(JSON.stringify(state));
      newState[payload] = true;
      return newState;
    },
    [REQUEST_HEADERS_FAIL]: (state, { payload }) => {
      const newState = JSON.parse(JSON.stringify(state));
      newState[payload.filename] = false;
      return newState;
    },
    [REQUEST_HEADERS_SUCCESS]: (state, { payload }) => {
      const newState = JSON.parse(JSON.stringify(state));
      newState[payload.filename] = false;
      return newState;
    }
  }
);

const headersErrors = createReducer(
  {},
  {
    [REQUEST_HEADERS]: (state, { payload }) => ({
      ...state,
      [payload]: constant(null)
    }),
    [REQUEST_HEADERS_FAIL]: (state, { payload }) => ({
      ...state,
      [payload.filename]: payload.error
    })
  }
);

// Images upload
const uploadProgress = createReducer(null, {
  [UPDATE_IMG_PROGRESS]: (state, { payload }) => ({
    ...state,
    [payload.index]: (payload.progress.loaded / payload.progress.total) * 100
  }),
  [REQUEST_UPLOAD_IMG]: (state, { payload }) => ({
    ...state,
    [payload]: 0
  }),
  [REQUEST_UPLOAD_IMG_SUCCESS]: (state, { payload }) => ({
    ...state,
    [payload]: 100
  }),
  [REQUEST_UPLOAD_IMG_FAIL]: (state, { payload }) => ({
    ...state,
    [payload]: 0
  }),
  [CLEAR_MEDIA_UPLOAD_PROGRESS]: constant(null),
  [RESET_IMG_UPLOADS]: constant(null)
});

const uploadErrors = createReducer(null, {
  [REQUEST_UPLOAD_IMG_FAIL]: (state, { payload }) => ({
    ...state,
    [payload]: true
  }),
  [RESET_IMG_UPLOADS]: constant(null)
});

// Annotated Upload
const isUploadingAnnotated = createReducer(false, {
  [REQUEST_UPLOAD_ANNOTATED]: constant(true),
  [REQUEST_UPLOAD_ANNOTATED_SUCCESS]: constant(false),
  [REQUEST_UPLOAD_ANNOTATED_FAIL]: constant(false)
});

const uploadError = createReducer(null, {
  [REQUEST_UPLOAD_ANNOTATED]: constant(null),
  [REQUEST_UPLOAD_ANNOTATED_FAIL]: (state, { payload }) => payload
});

// Image Delete
const isDeletingImage = createReducer(null, {
  [REQUEST_DELETE_IMG]: (state, { payload }) => payload,
  [REQUEST_DELETE_IMG_SUCCESS]: constant(null),
  [REQUEST_DELETE_IMG_FAIL]: constant(null)
});

const deleteError = createReducer(null, {
  [REQUEST_DELETE_IMG]: constant(null),
  [REQUEST_DELETE_IMG_FAIL]: (state, { payload }) => payload
});

// Estimated Cost Upload
const isUploadingEstimatedCost = createReducer(false, {
  [REQUEST_UPLOAD_ESTIMATED_COST]: constant(true),
  [REQUEST_UPLOAD_ESTIMATED_COST_SUCCESS]: constant(false),
  [REQUEST_UPLOAD_ESTIMATED_COST_FAIL]: constant(false)
});

const estimatedCostError = createReducer(null, {
  [REQUEST_UPLOAD_ESTIMATED_COST]: constant(null),
  [REQUEST_UPLOAD_ESTIMATED_COST_FAIL]: (state, { payload }) => payload
});

// Description Upload
const isUploadingDescription = createReducer(false, {
  [REQUEST_UPLOAD_DESCRIPTION]: constant(true),
  [REQUEST_UPLOAD_DESCRIPTION_SUCCESS]: constant(false),
  [REQUEST_UPLOAD_DESCRIPTION_FAIL]: constant(false)
});

const descriptionError = createReducer(null, {
  [REQUEST_UPLOAD_DESCRIPTION]: constant(null),
  [REQUEST_UPLOAD_DESCRIPTION_FAIL]: (state, { payload }) => payload
});

export default combineReducers({
  currentCase,
  data,
  deleteError,
  descriptionError,
  error,
  estimatedCostError,
  headersErrors,
  imageHeaders,
  isDeletingImage,
  isDeletingPhysicalDamage,
  isLoading,
  isLoadingImage,
  isLoadingList,
  list,
  listError,
  requestImageError,
  isUploadingAnnotated,
  isUploadingDescription,
  isUploadingEstimatedCost,
  loadingHeaders,
  uploadError,
  uploadErrors,
  uploadProgress
});
